/**
 * Chunk-load recovery for rolling-update version skew (issue #14964, part of
 * #14861).
 *
 * ## Why
 *
 * During a rolling update old and new pods serve traffic together. Each pod
 * serves only its own build's `/_next/static`, and there is no shared
 * `assetPrefix`, so a page rendered by one build can request a chunk that
 * lands on a pod running the other and gets a 404. The browser surfaces that
 * as a `ChunkLoadError` (or a failed dynamic import / failed `<script>` load)
 * and the user is left on a broken page until they reload.
 *
 * The recovery is that reload, done for them: ONE hard reload per
 * deployment, which re-renders the page against a single build. Serving
 * older builds' static assets from a bucket is the fuller fix and is
 * deliberately deferred (#14964's 2026-10-06 decision).
 *
 * ## Why once per deployment, and why storage failure means "no reload"
 *
 * A chunk that is genuinely missing (a bad deploy rather than skew) fails
 * again after the reload. Without a guard that outlives the reload itself the
 * page would reload forever, so the guard lives in `sessionStorage`: it
 * records every deployment id (Next's `deploymentId`, the build commit SHA)
 * this tab has reloaded for, and reloads only when the page is running one
 * it has not. Remembering only the LAST id would loop mid-rollout: with old
 * and new pods both serving, a reload can land on either build, so the tab
 * could alternate A, B, A and reload every time. With the whole set, each
 * build gets at most one reload, and the NEXT deploy that day still gets its
 * own recovery.
 *
 * Before #15676 the guard was once per session, so a tab left open all day
 * recovered from the first deploy and was stuck on every later one. When no
 * deployment id can be read, that once-per-session rule is still the
 * fallback.
 *
 * When storage cannot be read, written, or does not keep the write, the guard
 * cannot prove a reload has not already happened — so it does not reload.
 * A stuck page is recoverable by the user; a reload loop is not.
 *
 * `src/app/global-error.tsx` carries an inline copy of this guard (it may
 * not import app modules, #5544). Both use the same storage key and value
 * semantics; `tests/unit/app/global-error-chunk-recovery-15676.test.tsx`
 * pins that.
 *
 * ## Why the reload goes through `safeRefresh`
 *
 * `safeRefresh` is the repo's reload-without-losing-work primitive: it
 * flushes registered form drafts before calling the one approved
 * `window.location.reload()` site. A chunk failure mid-form should not cost
 * the user their draft.
 *
 * Registered from `src/instrumentation-client.ts`, which Next runs before the
 * app hydrates, so the listeners are in place before the first lazy chunk is
 * requested.
 */

import { safeRefresh } from '@/lib/safe-refresh'

/**
 * `sessionStorage` key recording the deployment ids this tab has done a
 * chunk-load reload for. Shared with the inline guard in
 * `src/app/global-error.tsx` — change both together.
 */
export const CHUNK_LOAD_RELOAD_SESSION_KEY = 'cadence-chunk-load-reload'

/**
 * The value written under {@link CHUNK_LOAD_RELOAD_SESSION_KEY} when the
 * deployment id is unknown — the once-per-session fallback.
 */
const UNKNOWN_DEPLOYMENT_MARKER = '1'

/**
 * Separates the remembered deployment ids in the stored value. Ids are
 * commit SHAs, so they never contain it.
 */
const DEPLOYMENT_ID_SEPARATOR = ' '

/**
 * Most deployment ids a tab remembers. A handful of deploys a day keeps a
 * tab far below it; the bound only stops the value growing without limit.
 */
const MAX_REMEMBERED_DEPLOYMENTS = 20

/** Query parameter Next appends to build assets: `?dpl=<deploymentId>`. */
const DEPLOYMENT_ID_QUERY_PARAM = 'dpl'

/** Path prefix every Next build-output asset is served from. */
const NEXT_STATIC_PATH = '/_next/static/'

/** `Error.name` webpack and Turbopack give a failed chunk load. */
const CHUNK_LOAD_ERROR_NAME = 'ChunkLoadError'

/**
 * Message shapes that mean "a build chunk failed to load" on their own,
 * wherever the URL is:
 *  - webpack JS / CSS chunks: `Loading chunk 123 failed`,
 *    `Loading CSS chunk 123 failed`
 *  - Turbopack: `Failed to load chunk /_next/static/... from module ...`
 *  - Safari's dynamic-import failure, which carries no URL at all:
 *    `Importing a module script failed.`
 */
const CHUNK_LOAD_MESSAGE_PATTERNS: readonly RegExp[] = [
  /Loading (CSS )?chunk [^\s]+ failed/i,
  /Failed to load chunk /i,
  /Importing a module script failed/i,
]

/**
 * Dynamic-import failure messages (Chrome, Firefox) that carry the module
 * URL. These count only when that URL is one of this app's Next static
 * assets, so a failed third-party `import()` does not spend the deployment's
 * one reload.
 */
const DYNAMIC_IMPORT_MESSAGE_PATTERNS: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
]

function readMessage(reason: unknown): string | null {
  if (typeof reason === 'string') return reason
  if (typeof reason === 'object' && reason !== null && 'message' in reason) {
    const { message } = reason as { message: unknown }
    return typeof message === 'string' ? message : null
  }
  return null
}

/**
 * The prefix a URL must carry to be one of this app's static assets. With
 * the page origin known it is origin-qualified, so another origin's
 * `/_next/static/` (an embedded Next app, a third-party widget) does not
 * match; without it (non-browser callers) the bare path is the best signal.
 */
function ownStaticPrefix(origin: string | undefined): string {
  return origin ? `${origin}${NEXT_STATIC_PATH}` : NEXT_STATIC_PATH
}

/**
 * True when `reason` is a failed load of one of this app's build chunks.
 * `origin` is the page origin; pass it so a URL-bearing failure from another
 * origin is not mistaken for this app's.
 */
export function isChunkLoadError(reason: unknown, origin?: string): boolean {
  if (typeof reason === 'object' && reason !== null) {
    const { name } = reason as { name?: unknown }
    if (name === CHUNK_LOAD_ERROR_NAME) return true
  }

  const message = readMessage(reason)
  if (message === null) return false
  if (message.includes(CHUNK_LOAD_ERROR_NAME)) return true
  if (CHUNK_LOAD_MESSAGE_PATTERNS.some(pattern => pattern.test(message))) {
    return true
  }
  return (
    message.includes(ownStaticPrefix(origin)) &&
    DYNAMIC_IMPORT_MESSAGE_PATTERNS.some(pattern => pattern.test(message))
  )
}

/**
 * True when `url` is one of this app's Next static assets. Relative URLs
 * resolve against `origin`; with no origin, only the path is checked.
 */
function isOwnStaticAssetUrl(
  url: unknown,
  origin: string | undefined,
): boolean {
  if (typeof url !== 'string') return false
  if (!origin) return url.includes(NEXT_STATIC_PATH)
  try {
    const parsed = new URL(url, origin)
    return (
      parsed.origin === origin && parsed.pathname.startsWith(NEXT_STATIC_PATH)
    )
  } catch (_unparseableUrl) {
    return false
  }
}

/**
 * True when a resource `error` event's target is a `<script>` or stylesheet
 * `<link>` for one of this app's Next static assets (same origin as
 * `origin`, when given). Preload links are excluded: a failed prefetch is
 * not a load the current page needs, and the real load (if it ever happens)
 * reports on its own.
 */
export function isStaticAssetLoadFailure(
  target: unknown,
  origin?: string,
): boolean {
  if (typeof target !== 'object' || target === null) return false
  const { tagName, src, href, rel } = target as {
    tagName?: unknown
    src?: unknown
    href?: unknown
    rel?: unknown
  }
  if (typeof tagName !== 'string') return false

  switch (tagName.toUpperCase()) {
    case 'SCRIPT':
      return isOwnStaticAssetUrl(src, origin)
    case 'LINK':
      return rel === 'stylesheet' && isOwnStaticAssetUrl(href, origin)
    default:
      return false
  }
}

export interface DeploymentIdSources {
  /** Next's client global, `globalThis.NEXT_DEPLOYMENT_ID`. */
  globalDeploymentId?: unknown
  /** `src` of every `<script>` on the page. */
  scriptSrcs: readonly string[]
  /** The page origin, to keep another origin's `/_next/static` out. */
  origin?: string
}

/**
 * The deployment id the running page was built as, or `null` when it cannot
 * be read. Next exposes it as `globalThis.NEXT_DEPLOYMENT_ID`; failing that,
 * it is the `dpl` query on the page's own `/_next/static` script URLs.
 */
export function readDeploymentId(sources: DeploymentIdSources): string | null {
  const { globalDeploymentId, scriptSrcs, origin } = sources
  if (typeof globalDeploymentId === 'string' && globalDeploymentId !== '') {
    return globalDeploymentId
  }
  for (const src of scriptSrcs) {
    if (!isOwnStaticAssetUrl(src, origin)) continue
    try {
      const id = new URL(src, origin ?? 'http://localhost').searchParams.get(
        DEPLOYMENT_ID_QUERY_PARAM,
      )
      if (id) return id
    } catch (_unparseableUrl) {
      // Not a URL we can read; try the next script.
    }
  }
  return null
}

function readPageDeploymentId(): string | null {
  return readDeploymentId({
    globalDeploymentId: (globalThis as { NEXT_DEPLOYMENT_ID?: unknown })
      .NEXT_DEPLOYMENT_ID,
    scriptSrcs: Array.from(document.scripts, script => script.src),
    origin: window.location.origin,
  })
}

type SessionStore = Pick<Storage, 'getItem' | 'setItem'>

export interface ChunkLoadRecoveryDeps {
  /** Resolves the session store. May throw (e.g. storage blocked). */
  getStorage: () => SessionStore
  /**
   * The deployment the page is running, or `null` when unknown. Omitted or
   * throwing means unknown, which falls back to once per session.
   */
  getDeploymentId?: () => string | null
  /** Performs the hard reload. */
  reload: () => void
}

const defaultDeps: ChunkLoadRecoveryDeps = {
  getStorage: () => window.sessionStorage,
  getDeploymentId: readPageDeploymentId,
  reload: safeRefresh,
}

function resolveDeploymentId(deps: ChunkLoadRecoveryDeps): string | null {
  try {
    return deps.getDeploymentId?.() || null
  } catch (_unreadable) {
    return null
  }
}

/**
 * Reloads the page unless this tab already reloaded for the deployment it is
 * running — any deployment it has reloaded for, not only the last one, so a
 * mid-rollout A, B, A sequence cannot loop. With no readable deployment id,
 * reloads unless this session already reloaded at all. Returns whether it
 * reloaded. Any storage failure — including a write that does not stick —
 * resolves to "no reload"; see the file header for why.
 */
export function reloadOncePerDeployment(
  deps: ChunkLoadRecoveryDeps = defaultDeps,
): boolean {
  const deploymentId = resolveDeploymentId(deps)
  try {
    const storage = deps.getStorage()
    const previous = storage.getItem(CHUNK_LOAD_RELOAD_SESSION_KEY)
    const remembered = (previous ?? '')
      .split(DEPLOYMENT_ID_SEPARATOR)
      .filter(id => id !== '')
    if (deploymentId === null) {
      if (previous !== null) return false
    } else if (remembered.includes(deploymentId)) {
      return false
    }
    const marker =
      deploymentId === null
        ? UNKNOWN_DEPLOYMENT_MARKER
        : [...remembered, deploymentId]
            .slice(-MAX_REMEMBERED_DEPLOYMENTS)
            .join(DEPLOYMENT_ID_SEPARATOR)
    storage.setItem(CHUNK_LOAD_RELOAD_SESSION_KEY, marker)
    if (storage.getItem(CHUNK_LOAD_RELOAD_SESSION_KEY) !== marker) {
      return false
    }
  } catch (_storageUnavailable) {
    // The guard cannot be recorded, so a reload could loop. Leave the page
    // as it is; the user can still reload by hand.
    return false
  }

  deps.reload()
  return true
}

type Listener = (event: unknown) => void

/** The slice of `Window` the recovery listens on. */
export interface ChunkLoadEventTarget {
  /** The page origin, used to tell this app's assets from another origin's. */
  location?: { origin?: string }
  addEventListener(type: string, listener: Listener, capture?: boolean): void
  removeEventListener(type: string, listener: Listener, capture?: boolean): void
}

/**
 * Listens for chunk-load failures and reloads once per deployment. Covers:
 *  - `unhandledrejection` whose reason is a chunk-load error (lazy chunks and
 *    dynamic `import()`)
 *  - `error` events carrying a chunk-load error or message
 *  - resource `error` events from a failed Next static `<script>` or
 *    stylesheet `<link>` — these do not bubble, hence the capture phase.
 *
 * Returns a function that removes the listeners.
 */
export function installChunkLoadRecovery(
  target: ChunkLoadEventTarget,
  deps: ChunkLoadRecoveryDeps = defaultDeps,
): () => void {
  const origin = target.location?.origin || undefined

  const onError: Listener = event => {
    const {
      error,
      message,
      target: source,
    } = (event ?? {}) as {
      error?: unknown
      message?: unknown
      target?: unknown
    }
    if (
      isChunkLoadError(error, origin) ||
      isChunkLoadError(message, origin) ||
      isStaticAssetLoadFailure(source, origin)
    ) {
      reloadOncePerDeployment(deps)
    }
  }

  const onUnhandledRejection: Listener = event => {
    const { reason } = (event ?? {}) as { reason?: unknown }
    if (isChunkLoadError(reason, origin)) reloadOncePerDeployment(deps)
  }

  target.addEventListener('error', onError, true)
  target.addEventListener('unhandledrejection', onUnhandledRejection)

  return () => {
    target.removeEventListener('error', onError, true)
    target.removeEventListener('unhandledrejection', onUnhandledRejection)
  }
}
